// Logic thuần cho vùng ký trên PDF biên bản thỏa thuận.
// Tách khỏi component để test được và để đổi ruột viewer không ảnh hưởng chỗ khác.
import {
  PDFJS_VIEWER_PATH,
  SIGN_ZONE_INIT,
} from "constants/AgreementSignZoneConstant";

const deepClone = value => JSON.parse(JSON.stringify(value));

/**
 * Dựng sign_zones ban đầu cho form.
 * @param {Array | null | undefined} savedSignZones vùng ký đã lưu từ agreement-info
 * @returns {Array} bản sao sâu — sửa thoải mái không đụng hằng gốc
 */
export const buildSignZonesSeed = savedSignZones => {
  if (Array.isArray(savedSignZones) && savedSignZones.length > 0) {
    return deepClone(savedSignZones);
  }
  return deepClone(SIGN_ZONE_INIT);
};

/**
 * Tạo một zone_info rỗng cho một bên ký.
 * zone_id là định danh dùng chung với annotation bên pdf.js nên phải duy nhất.
 * @param {object} signZone một phần tử của sign_zones
 */
export const createZoneInfo = signZone => {
  const random = (Math.random() + 1).toString(36).substring(6).toUpperCase();
  const { n_action_code: nActionCode, activity } = signZone;

  return {
    zone_id: `${nActionCode}__${random}__${activity.value}`,
    zone_key: random,
    zone_field: { ...activity },
    zone_is_created: false,
    zone_page: 0,
    zone_page_width: 0,
    zone_page_height: 0,
    zone_x: 0,
    zone_y: 0,
    zone_width: 0,
    zone_height: 0,
  };
};

/**
 * Định dạng ngày theo chuẩn chuỗi ngày của PDF: D:YYYYMMDDHHmmss+HH'mm'
 * @param {Date} date
 */
export const formatPDFDate = date => {
  const pad = (n, width = 2) => n.toString().padStart(width, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  const offset = -date.getTimezoneOffset(); // đơn vị phút
  const offsetSign = offset >= 0 ? "+" : "-";
  const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);

  return `D:${year}${month}${day}${hours}${minutes}${seconds}${offsetSign}${offsetHours}'${offsetMinutes}'`;
};

/**
 * Dựng object annotation khung ký (Square) để gửi sang iframe pdf.js.
 * Cấu trúc bám sát pdfjs-annotation-extension — sai một thuộc tính là khung
 * không hiện.
 * @param {object} params
 * @param {string} params.id trùng với zone_id của zone_info
 * @param {number} params.pageNumber 1-based theo pdf.js
 * @param {string} params.stroke màu viền
 * @param {number} params.x Tọa độ x của khung ký
 * @param {number} params.y Tọa độ y của khung ký
 * @param {number} params.width Chiều rộng của khung ký
 * @param {number} params.height Chiều cao của khung ký
 * @param {string} [params.title] Tiêu đề của annotation (mặc định: "Requestor")
 * @param {string} [params.contentsObjText] Nội dung văn bản trong annotation (mặc định: "")
 * @param {boolean} [params.readonly] Cho phép chỉnh sửa annotation không (mặc định: false)
 * @param {Date | null} [params.date] Ngày tạo annotation (mặc định: ngày hiện tại)
 */
export const initAnnotation = ({
  id,
  x,
  y,
  width,
  height,
  pageNumber,
  stroke,
  title = "Requestor",
  contentsObjText = "",
  readonly = false,
  date = null,
}) => {
  const konvaClientRect = { x, y, width, height, stroke };

  const konvaString = {
    attrs: {
      name: "PdfjsAnnotationExtension_shape_group",
      id,
    },
    className: "Group",
    children: [
      {
        attrs: {
          x,
          y,
          width,
          height,
          strokeScaleEnabled: false,
          stroke,
        },
        className: "Rect",
      },
    ],
  };

  return {
    id,
    pageNumber,
    konvaString: JSON.stringify(konvaString),
    konvaClientRect,
    title,
    type: 5,
    pdfjsType: 5,
    pdfjsEditorType: 15,
    subtype: "Square",
    color: stroke,
    date: date || formatPDFDate(new Date()),
    contentsObj: { text: contentsObjText },
    comments: [],
    readonly,
    fontSize: 14,
    draggable: !readonly,
    resizable: !readonly,
    // Khung ký không phải comment nên không mở popup nhập nội dung
    isNotOpenCustomComment: true,
  };
};

/**
 * Dựng URL iframe viewer pdf.js.
 * Dùng PUBLIC_URL để chạy đúng cả khi build có prefix đường dẫn.
 * @param {string} filePdfUrl thường là blob URL do parent tạo (iframe cùng
 *   origin nên đọc được)
 * @param {string} userName tên hiển thị trong annotation
 * @param {number} reloadNonce đổi giá trị để buộc iframe nạp lại tài liệu mới.
 *   Cần vì bundle annotation-extension nặng ~3MB, có lần về thiếu byte giữa
 *   chừng; module hỏng thì im lặng, viewer không bao giờ báo documentloaded.
 */
export const buildViewerUrl = (filePdfUrl, userName = "", reloadNonce = 0) => {
  const viewerUrl = new URL(
    `${process.env.PUBLIC_URL || ""}${PDFJS_VIEWER_PATH}`,
    window.location.origin
  );
  viewerUrl.searchParams.append("file", filePdfUrl);

  if (reloadNonce) {
    viewerUrl.searchParams.append("_r", String(reloadNonce));
  }

  return `${viewerUrl.toString()}#ae_username=${userName}&ae_default_sidebar_open=false`;
};

/**
 * Chuyển sự kiện annotationChange của pdf.js thành lệnh cập nhật form.
 * @param {Array} signZones giá trị hiện tại của field sign_zones
 * @param {object} annotationChange payload từ iframe
 * @returns {{ path: string, value: object } | null} null khi không khớp vùng ký nào
 */
export const applyAnnotationChange = (signZones, annotationChange) => {
  const { id, pageNumber, konvaClientRect } = annotationChange || {};

  if (!Array.isArray(signZones) || !id || !konvaClientRect) {
    return null;
  }

  for (let zoneIndex = 0; zoneIndex < signZones.length; zoneIndex += 1) {
    const zoneInfos = signZones[zoneIndex]?.zone_infos || [];
    const infoIndex = zoneInfos.findIndex(item => item.zone_id === id);

    if (infoIndex >= 0) {
      return {
        path: `sign_zones.${zoneIndex}.zone_infos.${infoIndex}`,
        value: {
          ...zoneInfos[infoIndex],
          // pdf.js đánh số trang từ 1, sign_zones lưu từ 0
          zone_page: Number(pageNumber) - 1,
          zone_x: konvaClientRect.x,
          zone_y: konvaClientRect.y,
          zone_width: konvaClientRect.width,
          zone_height: konvaClientRect.height,
        },
      };
    }
  }

  return null;
};
